/* eslint-disable no-plusplus */
import * as R from 'ramda'
import {
  ClientMessage,
  DeletedMessage,
  EventDeleteMessagesPayload,
  IMessage,
  Message,
  MessageRange,
} from '@weblite/ts-action/message'
import { Room } from '@weblite/ts-action/room'
import {
  getImageThumbnail,
  intelligentCompressImage,
} from '$func/image-compression'
import getBlobSrc from '$func/getBlobSrc'
import { getImageDimension } from '$func/image-dimension'
import {
  PendingUploadAudioAttachment,
  PendingUploadFileAttachment,
  PendingUploadImageAttachment,
  PendingUploadVideoAttachment,
  PendingUploadVoiceAttachment,
} from '$logic/pending-uploads/types'
import { getVideoDimension } from '$func/video-dimension'
import { getVideoScreenShot } from '$func/video-screen-shot'
import { getAudioDuration } from '$func/audio-duration'
import { blobToFile } from '$func/file'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import { showFileMessageLimitErrorSnackBar } from '$comp/snackbars'
import { VALIDATE_MESSAGE_FILE_SIZE_ENABLED } from '$config/env'
import { RecordedVoice } from '$comp/_app/components/chat/components/footer/components/send-field/components/voice-recorder/types'

export const MAX_FILE_MESSAGE_SIZE_MB = 300

export function calculateMissingMessageRange(
  range: MessageRange,
  messages: ClientMessage[],
  undeletedCount: number,
): MessageRange {
  if (!messages.length) return range

  if ('range' in range) {
    const [start, end] = range.range
    const messagesLength = messages.length
    let finalStart
    let finalEnd

    let currentIid = start
    // eslint-disable-next-line no-restricted-syntax
    for (const { iid } of messages) {
      if (iid !== currentIid) {
        finalStart = currentIid
        break
      }
      currentIid++
    }
    if (!finalStart) finalStart = currentIid

    currentIid = end
    for (let i = messagesLength - 1; i >= 0; i--) {
      const { iid } = messages[i]
      if (iid !== currentIid) {
        finalEnd = currentIid
        break
      }
      currentIid--
    }
    if (!finalEnd) finalEnd = currentIid

    return { range: [finalStart, finalEnd] }
  }

  const { count, position, iid } = range
  if (
    messages[messages.length - 1].iid === iid - 1 &&
    position === 'PREVIOUS'
  ) {
    return {
      iid: messages[0].iid,
      position,
      count: count - undeletedCount,
    }
  }

  if (messages[0].iid === iid + 1 && position === 'NEXT') {
    return {
      iid: messages[messages.length - 1].iid,
      position,
      count: count - messages.length,
    }
  }

  return range
}

function createDeletedMessage(
  roomId: string,
  iid: number,
): DeletedMessage & { id: string } {
  // TODO should include `date`?`
  return { roomId, iid, patches: { deleted: true }, id: `${roomId}_${iid}` }
}

function fillRange(
  messages: Message[],
  start: number,
  end: number,
  roomId: string,
) {
  const items: ClientMessage[] = []
  let index = 0
  for (let i = start; i <= end; i++) {
    const message = messages[index]
    if (message?.iid === i) {
      items.push(message)
      index++
    } else {
      items.push(createDeletedMessage(roomId, i))
    }
  }
  return items
}

export async function fillDeletedMessages(
  messages: Message[],
  range: MessageRange,
  room: Room,
): Promise<ClientMessage[]> {
  if ('range' in range) {
    const [start, end] = range.range
    if (messages.length === end - start + 1) return messages

    return fillRange(messages, start, end, room.id)
  }

  const { iid, count, position } = range

  let start
  let end
  if (position === 'NEXT') {
    start = Math.max(iid + 1, 1)
    // If server returned fewer messages, it means others are deleted
    end = Math.max(Math.min(room.iid, iid + count), R.last(messages)?.iid ?? 0)
  } else {
    messages.reverse()
    start = Math.min(Math.max(1, iid - count), messages[0]?.iid ?? Infinity)
    end = Math.min(iid - 1, room.iid)
  }
  return fillRange(messages, start, end, room.id)
}

export function groupDeletePatches(
  patches: Pick<IMessage, 'iid' | 'roomId'>[],
): EventDeleteMessagesPayload[] {
  const grouped = R.groupBy(R.prop('roomId'), patches)
  return R.keys(grouped).map(roomId => ({
    roomId,
    iids: R.pluck('iid', grouped[roomId]),
  }))
}

export async function getImageMessageDataFromFile(file: File) {
  const image = await intelligentCompressImage(file)
  const imageThumbnail = await getImageThumbnail(image).then(getBlobSrc)
  const { width, height } = await getImageDimension(image)
  return { image, imageThumbnail, width, height }
}

export async function getImageMessageAttachmentFromFile(file: File) {
  const { image, imageThumbnail, width, height } =
    await getImageMessageDataFromFile(file)
  const attachment: PendingUploadImageAttachment = {
    base64: imageThumbnail,
    cropped: null, // TODO
    height,
    width,
    size: image.size,
  }
  return { image, attachment }
}

export async function getVideoMessageDataFromFile(file: File) {
  const { width, height, duration } = await getVideoDimension(file)
  const imageBlob = await getVideoScreenShot(file)

  const videoThumbnail = await getImageThumbnail(
    blobToFile(imageBlob, 'image'),
    100,
  ).then(getBlobSrc)

  const videoHighQualityThumbnail = await getImageThumbnail(
    blobToFile(imageBlob, 'image.jpg'),
    400,
  )

  return { width, height, duration, videoThumbnail, videoHighQualityThumbnail }
}
export async function getVideoAttachmentFromFile(
  file: File,
  compressionHeight?: number,
) {
  const { width, height, duration, videoThumbnail, videoHighQualityThumbnail } =
    await getVideoMessageDataFromFile(file)

  const pendingAttachment: PendingUploadVideoAttachment = {
    base64: videoThumbnail,
    height,
    width,
    size: file.size,
    filename: file.name,
    duration,
    compressionHeight,
  }
  return { pendingAttachment, videoHighQualityThumbnail }
}

export async function getAudioAttachmentFromFile(file: File) {
  const { duration } = await getAudioDuration(file)

  const pendingAttachment: PendingUploadAudioAttachment = {
    filename: file.name,
    size: file.size,
    duration,
    cover: '', // TODO
  }
  return pendingAttachment
}

export function getFileAttachmentFromFile(file: File) {
  const pendingAttachment: PendingUploadFileAttachment = {
    filename: file.name || '',
    size: file.size,
  }
  return pendingAttachment
}

export function convertVoiceToFile(voice: RecordedVoice) {
  return new File([voice.blob], `voice_${dateWithOffset()}.${voice.ext}`, {
    type: voice.blob.type,
  })
}

export async function getVoiceAttachmentFromFile(
  file: File,
  voice: RecordedVoice,
) {
  const attachment: PendingUploadVoiceAttachment = {
    wave: voice.wave,
    size: file.size,
    duration: voice.duration,
  }
  return attachment
}

export function calculateValidMessageRange(
  boundaries: { lowerBound: number; upperBound: number },
  messageRange: MessageRange,
): MessageRange | undefined {
  if ('range' in messageRange) {
    const [start, end] = messageRange.range
    const range = [
      Math.max(start, boundaries.lowerBound),
      Math.min(end, boundaries.upperBound),
    ] as [number, number]
    if (range[1] >= range[0]) return { range }
    return
  }
  const { position, iid } = messageRange

  if (position === 'NEXT') {
    const count = Math.min(messageRange.count, boundaries.upperBound - iid)
    if (count)
      return { position, iid: Math.max(iid, boundaries.lowerBound - 1), count }
    return
  }

  const count = Math.min(messageRange.count, iid - boundaries.lowerBound)
  if (count > 0)
    return { position, iid: Math.min(iid, boundaries.upperBound + 1), count }
}

export function validateMessageFileSizeAndShowError(file: File | undefined) {
  if (!VALIDATE_MESSAGE_FILE_SIZE_ENABLED) return true
  if (!file) return false
  if (file.size < MAX_FILE_MESSAGE_SIZE_MB * 1024 * 1024) return true
  showFileMessageLimitErrorSnackBar()
  return false
}
